/**
 * Discovers the links that lead to a tracked destination, and reports when
 * one is actually seen or clicked.
 *
 * The whole difficulty here is Swup. This theme's Swup container is `#main`,
 * and header and footer are template parts OUTSIDE it, so:
 *
 *  - header and footer links are the SAME DOM nodes for the whole session.
 *    Re-scanning after every navigation would attach a second observer to a
 *    node that already has one and double-count the header button. The
 *    WeakSet below is what prevents that.
 *  - links inside `#main` are destroyed and rebuilt on every navigation, so
 *    they DO need re-scanning, and their old observations have to be dropped.
 *
 * That same structural split is what makes placement detection reliable:
 * anything outside `#main` is site-wide chrome, anything inside it is page
 * content.
 */

import type { CtaRef, PublicDestination } from '../types.ts';
import { fnv1a } from '../lib/hash.ts';
import { normalizePath } from '../lib/url-match.ts';

export interface CtaHit {
	destination: PublicDestination;
	cta: CtaRef;
}

/** Every anchor already observed, so persistent chrome is never bound twice. */
const observed = new WeakSet<Element>();

/** Anchor → its resolved hits, so the click handler does no work. */
const registry = new WeakMap<Element, CtaHit[]>();

let observer: IntersectionObserver | null = null;

const placementOf = (el: Element): CtaRef['p'] => {
	if (el.closest('[role="dialog"], dialog, .modal, .expert-modal-content')) return 'modal';
	if (el.closest('#main')) return 'content';
	if (el.closest('footer, .site-footer')) return 'footer';
	if (el.closest('header, .site-header')) return 'header';
	// Outside #main and outside a recognisable header or footer — still
	// site-wide chrome rather than page content.
	return 'header';
};

const labelOf = (el: Element): string => {
	const aria = el.getAttribute('aria-label');
	const text = (aria ?? el.textContent ?? '').replace(/\s+/g, ' ').trim();
	if (text) return text.slice(0, 190);
	const img = el.querySelector('img[alt]');
	return (img?.getAttribute('alt') ?? '').replace(/\s+/g, ' ').trim().slice(0, 190);
};

/**
 * Same-origin path of a link, or null when it points anywhere else. Mirrors
 * the server-side check so a link that would be rejected there is never sent.
 */
const pathOf = (el: HTMLAnchorElement): string | null => {
	const raw = el.getAttribute('href') ?? '';
	if (!raw || raw.startsWith('#') || /^(mailto|tel|javascript|data):/i.test(raw)) return null;
	try {
		const url = new URL(raw, window.location.href);
		if (url.origin !== window.location.origin) return null;
		return normalizePath(url.pathname);
	} catch {
		return null;
	}
};

const keyFor = (destKey: string, path: string, label: string, placement: string): string =>
	fnv1a(`${destKey}|${path}|${label}|${placement}`).toString(16).padStart(8, '0');

/**
 * Scan a subtree for links pointing at any destination and start observing
 * them. `matches` decides whether a destination applies to a given path, and
 * `skip` excludes the destination the visitor is currently on — counting the
 * header button as an impression on the very page it leads to would inflate
 * the denominator with people who have already arrived.
 */
export const scan = (
	root: ParentNode,
	destinations: PublicDestination[],
	matches: (destination: PublicDestination, path: string) => boolean,
	skip: (destination: PublicDestination) => boolean,
	onVisible: (hit: CtaHit) => void,
	postId: number,
): void => {
	if (!observer) {
		observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					for (const hit of registry.get(entry.target) ?? []) onVisible(hit);
				}
			},
			{ threshold: 0.5 },
		);
	}

	const anchors = root.querySelectorAll<HTMLAnchorElement>('a[href]');
	for (const anchor of anchors) {
		if (observed.has(anchor)) continue;
		const path = pathOf(anchor);
		if (path === null) continue;

		const hits: CtaHit[] = [];
		for (const destination of destinations) {
			if (skip(destination) || !matches(destination, path)) continue;
			const label = labelOf(anchor);
			const placement = placementOf(anchor);
			hits.push({
				destination,
				cta: {
					k: keyFor(destination.k, path, label, placement),
					l: label,
					h: path,
					p: placement,
					// Site-wide chrome has no meaningful source page: the same
					// node is shared by every page in the session.
					s: placement === 'content' || placement === 'modal' ? postId : 0,
				},
			});
		}
		if (hits.length === 0) continue;

		observed.add(anchor);
		registry.set(anchor, hits);
		observer.observe(anchor);
	}
};

/** Hits for the anchor a click landed on, if any. */
export const hitsForEvent = (target: EventTarget | null): CtaHit[] => {
	if (!(target instanceof Element)) return [];
	const anchor = target.closest('a[href]');
	if (!anchor) return [];
	return registry.get(anchor) ?? [];
};

/**
 * There is deliberately no teardown here. IntersectionObserver holds weak
 * references to the elements it observes, so the anchors Swup discards with
 * the old `#main` are collected on their own; and the WeakSet/WeakMap keyed
 * on those same nodes goes with them. An explicit sweep would either be a
 * no-op (querySelectorAll only ever returns connected nodes) or risk
 * unobserving the persistent header links, which must keep their single
 * registration for the whole session.
 */
